/**
 * useMonetizationState Hook
 * 
 * Tracks user monetization state including:
 * - Current plan (free, subscription, project, lifetime)
 * - Credits balance and purchase history
 * - Prompt dismissal timestamps with cooldown logic
 * 
 * Normalizes all Firestore Timestamps to JS Dates for consistent handling.
 * See Master Plan Section 13 for full specification.
 */

import { useState, useEffect, useCallback } from 'react'
import {
  doc,
  onSnapshot,
  updateDoc,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore'
import { db } from '../firebase'
import { useAuth } from '../context/AuthContext'

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000

/**
 * Safely convert Firestore Timestamp to JS Date
 * Handles: Timestamp, Date, null, undefined, pending serverTimestamp
 * 
 * @param {Timestamp|Date|null} value - Firestore timestamp field
 * @returns {Date|null} JS Date or null
 */
function toDate(value) {
  if (!value) return null

  // Already a Date
  if (value instanceof Date) return value

  // Firestore Timestamp
  if (value?.toDate && typeof value.toDate === 'function') {
    return value.toDate()
  }

  // Pending serverTimestamp (appears as null until resolved)
  return null
}

/**
 * Check if a dismissal timestamp is within cooldown period
 * 
 * @param {Date|null} dismissedAt - When prompt was dismissed
 * @param {number} cooldownMs - Cooldown period in milliseconds
 * @returns {boolean} True if still in cooldown
 */
function isWithinCooldown(dismissedAt, cooldownMs) {
  if (!dismissedAt || !(dismissedAt instanceof Date)) return false
  const elapsed = Date.now() - dismissedAt.getTime()
  return elapsed < cooldownMs
}

/**
 * Calculate months active since first credit purchase
 * 
 * Rules:
 * - If firstPurchaseDate is null/undefined/invalid → return null (force copy-only)
 * - Calculate calendar months between firstPurchaseDate and now
 * - Always return at least 1 (even if same month)
 * - Round down partial months
 * 
 * @param {Date|null} firstPurchaseDate - JS Date of first purchase
 * @returns {number|null} Number of months, or null if invalid
 */
export function getMonthsActive(firstPurchaseDate) {
  // If no valid date, cannot calculate → copy-only mode
  if (
    !firstPurchaseDate ||
    !(firstPurchaseDate instanceof Date) ||
    isNaN(firstPurchaseDate)
  ) {
    return null
  }

  const now = new Date()
  const yearDiff = now.getFullYear() - firstPurchaseDate.getFullYear()
  const monthDiff = now.getMonth() - firstPurchaseDate.getMonth()

  const totalMonths = yearDiff * 12 + monthDiff

  // Always at least 1 month (even if purchased this month)
  return Math.max(1, totalMonths)
}

/**
 * useMonetizationState Hook
 * 
 * @returns {Object} Monetization state and methods
 */
export function useMonetizationState() {
  const { user } = useAuth()
  const [state, setState] = useState({
    plan: 'free', // 'free' | 'subscription' | 'project' | 'lifetime'
    creditsBalance: 0,
    creditsPurchaseCount: 0,
    totalCreditsSpent: 0, // In cents, from actual purchase records
    firstPurchaseDate: null, // JS Date or null
    promptsDismissed: {
      timeline: null, // JS Date of dismissal (7-day cooldown)
      members: null, // JS Date (permanent dismissal)
      creditsShown: 0, // Count: 0, 1, or 2 (after 2, never show again)
    },
    membersTabVisited: false,
    loading: true,
    error: null,
  })

  // Subscribe to user's monetization data
  useEffect(() => {
    if (!user?.uid) {
      setState(prev => ({ ...prev, loading: false }))
      return
    }

    const userRef = doc(db, 'users', user.uid)
    const unsubscribe = onSnapshot(
      userRef,
      (snap) => {
        const data = snap.data()?.monetization || {}
        const promptsDismissed = data.promptsDismissed || {}

        setState({
          plan: data.plan || 'free',
          creditsBalance: data.creditsBalance || 0,
          creditsPurchaseCount: data.creditsPurchaseCount || 0,
          totalCreditsSpent: data.totalCreditsSpent || 0,
          // Normalize Timestamps to JS Dates
          firstPurchaseDate: toDate(data.firstPurchaseDate),
          promptsDismissed: {
            timeline: toDate(promptsDismissed.timeline),
            members: toDate(promptsDismissed.members),
            creditsShown: promptsDismissed.creditsShown || 0,
          },
          membersTabVisited: data.membersTabVisited || false,
          loading: false,
          error: null,
        })
      },
      (err) => {
        console.error('Error fetching monetization state:', err)
        setState(prev => ({ ...prev, loading: false, error: err.message }))
      }
    )

    return unsubscribe
  }, [user?.uid])

  // Check if Timeline prompt should show (7-day cooldown)
  const shouldShowTimelinePrompt = useCallback(() => {
    if (state.loading) return false
    if (state.plan === 'lifetime') return false
    const dismissed = state.promptsDismissed.timeline
    if (!dismissed) return true
    return !isWithinCooldown(dismissed, SEVEN_DAYS_MS)
  }, [state.loading, state.plan, state.promptsDismissed.timeline])

  // Check if Members prompt should show (once ever, permanently dismissed)
  const shouldShowMembersPrompt = useCallback(() => {
    if (state.loading) return false
    if (state.plan === 'lifetime') return false
    if (state.membersTabVisited) return false
    // If members was ever dismissed, never show again
    return state.promptsDismissed.members === null
  }, [state.loading, state.plan, state.membersTabVisited, state.promptsDismissed.members])

  // Check if Credits prompt should show (only after purchase #1 and #2, then never)
  const shouldShowCreditsPrompt = useCallback(() => {
    if (state.loading) return false
    if (state.plan === 'lifetime') return false
    const shown = state.promptsDismissed.creditsShown || 0
    // Show after purchase 1 (shown=0→1) and purchase 2 (shown=1→2)
    // After shown=2, never show again
    return shown < 2 && state.creditsPurchaseCount > shown
  }, [
    state.loading,
    state.plan,
    state.promptsDismissed.creditsShown,
    state.creditsPurchaseCount,
  ])

  // Ensure monetization subdocument exists
  const ensureMonetizationData = useCallback(async () => {
    if (!user?.uid) return
    
    const userRef = doc(db, 'users', user.uid)
    try {
      await setDoc(userRef, {
        monetization: {
          plan: 'free',
          creditsBalance: 0,
          creditsPurchaseCount: 0,
          totalCreditsSpent: 0,
          firstPurchaseDate: null,
          promptsDismissed: {
            timeline: null,
            members: null,
            creditsShown: 0,
          },
          membersTabVisited: false,
        }
      }, { merge: true })
    } catch (err) {
      console.error('Failed to initialize monetization data:', err)
    }
  }, [user?.uid])

  // Dismiss Timeline prompt (7-day cooldown)
  const dismissTimelinePrompt = useCallback(async () => {
    if (!user?.uid) return
    const userRef = doc(db, 'users', user.uid)
    try {
      await updateDoc(userRef, {
        'monetization.promptsDismissed.timeline': serverTimestamp(),
      })
    } catch (err) {
      // If document doesn't exist, create it first
      await ensureMonetizationData()
      await updateDoc(doc(db, 'users', user.uid), {
        'monetization.promptsDismissed.timeline': serverTimestamp(),
      })
    }
  }, [user?.uid, ensureMonetizationData])

  // Dismiss Members prompt (permanent)
  const dismissMembersPrompt = useCallback(async () => {
    if (!user?.uid) return
    const userRef = doc(db, 'users', user.uid)
    try {
      await updateDoc(userRef, {
        'monetization.promptsDismissed.members': serverTimestamp(),
        'monetization.membersTabVisited': true,
      })
    } catch (err) {
      await ensureMonetizationData()
      await updateDoc(doc(db, 'users', user.uid), {
        'monetization.promptsDismissed.members': serverTimestamp(),
        'monetization.membersTabVisited': true,
      })
    }
  }, [user?.uid, ensureMonetizationData])

  // Dismiss Credits prompt (increment counter)
  const dismissCreditsPrompt = useCallback(async () => {
    if (!user?.uid) return
    const userRef = doc(db, 'users', user.uid)
    const newCount = (state.promptsDismissed.creditsShown || 0) + 1
    try {
      await updateDoc(userRef, {
        'monetization.promptsDismissed.creditsShown': newCount,
      })
    } catch (err) {
      await ensureMonetizationData()
      await updateDoc(doc(db, 'users', user.uid), {
        'monetization.promptsDismissed.creditsShown': newCount,
      })
    }
  }, [user?.uid, state.promptsDismissed.creditsShown, ensureMonetizationData])

  // Mark Members tab as visited (without showing prompt)
  const markMembersTabVisited = useCallback(async () => {
    if (!user?.uid) return
    const userRef = doc(db, 'users', user.uid)
    try {
      await updateDoc(userRef, {
        'monetization.membersTabVisited': true,
      })
    } catch (err) {
      await ensureMonetizationData()
      await updateDoc(doc(db, 'users', user.uid), {
        'monetization.membersTabVisited': true,
      })
    }
  }, [user?.uid, ensureMonetizationData])

  // Check if a specific project is purchased
  const isProjectPurchased = useCallback((projectId) => {
    // Implementation depends on how purchased projects are tracked
    // Could be a subcollection or array in user doc
    // For now, return false - to be implemented with payment integration
    return false
  }, [])

  return {
    ...state,
    shouldShowTimelinePrompt,
    shouldShowMembersPrompt,
    shouldShowCreditsPrompt,
    dismissTimelinePrompt,
    dismissMembersPrompt,
    dismissCreditsPrompt,
    markMembersTabVisited,
    isProjectPurchased,
    getMonthsActive: () => getMonthsActive(state.firstPurchaseDate),
  }
}

export default useMonetizationState
